import type { UpdateAirportTransferPayload } from 'api/experiences'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import type { UpdateAirportTransferFormFields } from '../types'
import moment from 'moment'
import { INTL_LONG_DATE, INTL_SHORT_TIME_FORMAT_AM_PM, SHORT_TIME_FORMAT_24_HOUR } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { isolatePhoneNumber } from 'lib/format/formatPhoneNumber'
import type { IntlShape } from 'react-intl'

export function isLEDAirportTransfer(transfer: App.OrderTransferItem): boolean {
  return transfer.provider === 'led'
}

export function isReturnAirportTransfer(transfers: Array<App.OrderTransferItem>): boolean {
  const hasAirportToHotelTransfer = transfers.some(transfer => transfer.type === 'AIRPORT-TO-HOTEL')
  const hasHotelToAirportTransfer = transfers.some(transfer => transfer.type === 'HOTEL-TO-AIRPORT')

  return hasAirportToHotelTransfer && hasHotelToAirportTransfer
}

export function formatAirportTransferJourneyTitle(
  intl: IntlShape,
  transfers: Array<App.OrderTransferItem>,
): string {
  let journeyType = intl.formatMessage({
    id: '0MUI2WELMhyKvXfEBPtV',
    defaultMessage: 'One-way',
    description: 'One-way journey option for airport transfer search',
  })

  if (isReturnAirportTransfer(transfers)) {
    journeyType = intl.formatMessage({
      id: 'gZ3D7nXIwrZ40YWiUYsh',
      defaultMessage: 'Return',
      description: 'Return journey option displayed in the airport transfer search menu',
    })
  }

  const airportTransfer = intl.formatMessage({
    id: '9J2uwcz-e5vNrpFbxSV0',
    defaultMessage: 'Airport transfer',
    description: 'Inclusion filter category label for airport transfer inclusions in hotel search filters',
  })

  const airportTransferTitle = `${airportTransfer.charAt(0).toLocaleLowerCase(intl.locale)}${airportTransfer.slice(1)}`

  return `${journeyType} ${airportTransferTitle}`
}

export function formatTransferTravellers(
  travellers: App.OrderTransferItemTraveller,
) {
  const travellerText = formatOccupantsShort({ adults: travellers.adults, children: travellers.children })
  if (travellers.children > 0 && (travellers.childSeats > 0 || travellers.boosterSeats > 0)) {
    const intl = getCurrentIntl()
    const seatLabels = [
      travellers.childSeats > 0 ? intl.formatMessage(
        {
          id: 'dJ10dHFyVK1VciOaNqxm',
          defaultMessage: '{count, plural, one {# child seat} other {# child seats}}',
          description: 'Child seat count in airport transfer traveller summary',
        },
        { count: travellers.childSeats },
      ) : undefined,
      travellers.boosterSeats > 0 ? intl.formatMessage(
        {
          id: 'J6KndjpVUE9pjSpYny6v',
          defaultMessage: '{count, plural, one {# booster seat} other {# booster seats}}',
          description: 'Booster seat count in airport transfer traveller summary',
        },
        { count: travellers.boosterSeats },
      ) : undefined,
    ].filter((label): label is string => Boolean(label))

    return intl.formatMessage(
      {
        id: 'l3-uUT_6xGThwMDmWTrq',
        defaultMessage: '{travellers} ({seats})',
        description: 'Airport transfer traveller summary with child and booster seat counts in parentheses',
      },
      {
        travellers: travellerText,
        seats: intl.formatList(seatLabels, { type: 'unit' }),
      },
    )
  }
  return travellerText
}

export function buildAirportTransferPayload(fields: UpdateAirportTransferFormFields) {
  const { flightNumber, date, time, specialRequests, travellers, baggage, fromLocationName, fromLocationId, toLocationName, toLocationId, surchargeAUD, surchargeIDR } = fields
  const { adults, children, childSeats, boosterSeats } = travellers
  const { bags, oversizedBags } = baggage

  const payload: UpdateAirportTransferPayload = {
    op: 'update_airport_transfer',
    data: {
      flightNumber,
      fromLocationName,
      fromLocationId,
      toLocationName,
      toLocationId,
      day: moment(date).format('YYYY-MM-DD'),
      time,
      specialRequests,
      adults,
      children,
      childSeats,
      boosterSeats,
      bags,
      oversizedBags,
      surchargeAUD,
      surchargeIDR,
    },
  }

  return payload
}

export function airportTransfersUpdateRequired(airportTransfers: Array<App.OrderTransferItem>, experiences?: Record<string, App.ExperienceOffer>) {
  return airportTransfers.some((airportTransfer) => {
    const experience = experiences?.[airportTransfer.experienceId]
    return airportTransferInfoMissing(airportTransfer, experience)
  })
}

export function airportTransferInfoMissing(airportTransfer: App.OrderTransferItem, experience?: App.ExperienceOffer) {
  const isBaliTransfer = isBaliTransferSlug(experience?.slug)
  const isHotelToHotel = isBaliHotelToHotelTransfer(isBaliTransfer, airportTransfer.airport.code)
  return (
    (!isHotelToHotel && !airportTransfer.flightNumber) ||
    !airportTransfer.date ||
    !airportTransfer.time
  )
}

// If there are airport transfers and there are some after today we can update them.
export function isAirportTransferAfterToday(airportTransfers: Array<App.OrderTransferItem>) {
  const currentDate = moment()
  return airportTransfers.some((transfer) => {
    const targetDate = moment(transfer.date)
    return targetDate.isAfter(currentDate)
  })
}

// For the new Complimentary Experiences, we do not want to show cancelled items.
// But we do for want to show them for standalone purchased experiences.
export function filterAirportTransfers(airportTransfers: Array<App.OrderTransferItem>, includeComplimentary = true, includeCancelled = false): Array<App.OrderTransferItem> {
  return airportTransfers.filter(item =>
    item.accommodation_parent_item_id === null ||
    item.accommodation_parent_item_id === undefined ||
    (includeComplimentary && item.accommodation_parent_item_id && (includeCancelled || item.status !== 'cancelled')),
  )
}

export function isAirportTransferDateInNextTwoWeeks(transferDate: string) {
  const currentDate = moment()
  const targetDate = moment().add(14, 'days')

  return moment(transferDate) >= currentDate && moment(transferDate) <= targetDate
}

export function airportTransferNoPickupDetailsMessage(transferItem: App.OrderTransferItem, showAirportTransfersUpdate: boolean) {
  if (transferItem.pickupDate && transferItem.pickupTime) {
    const intl = getCurrentIntl()
    return intl.formatMessage(
      {
        id: 'HcBKx8hQNmhCc13llAab',
        defaultMessage: '{date} · {time}',
        description: 'Airport transfer pickup date and time summary on the my escapes bookings page',
      },
      {
        date: intlDateFormatter(transferItem.pickupDate, INTL_LONG_DATE, intl.locale),
        time: intlDateFormatter(
          moment(transferItem.pickupTime, [SHORT_TIME_FORMAT_24_HOUR]),
          INTL_SHORT_TIME_FORMAT_AM_PM,
          intl.locale,
        ),
      },
    )
  }
  const intlInstance = getCurrentIntl()
  if (airportTransferInfoMissing(transferItem) && showAirportTransfersUpdate) {
    return intlInstance.formatMessage({
      id: 'qbdgGA1WxWKOBttZHRXu',
      defaultMessage: 'Estimated pickup time provided once form is submitted',
      description: 'Airport transfer message shown when pickup time will be provided after form submission',
    })
  }
  return intlInstance.formatMessage({
    id: 'dyayqfDk0NPQNG3vlXIF',
    defaultMessage: 'Your pick-up time will be confirmed within 7 days of your transfer date',
    description: 'Airport transfer message about pickup time confirmation timeline',
  })
}

// ICS vendor IDs are the same in test and prod.
export const ICS_VENDOR_IDS = [
  '0012y00000W105xAAB',
  '0012y00000dbTjvAAE',
  '0010I000026fcwcQAA',
  '0010I00002J0Bb0QAF',
  '0012y00000jypSJAAY',
  '0012y00000W0foQAAR',
  '0012y00000ddqE4AAI',
]

export type IcsContactRegion = 'thailand' | 'vietnam' | 'general'

export const ICS_CONTACT_NUMBERS: Record<IcsContactRegion, { number: string, humanReadable: string }> = {
  thailand: {
    number: '6281139603800',
    humanReadable: isolatePhoneNumber('+62 811 3960 3800'),
  },
  vietnam: {
    number: '6281139606800',
    humanReadable: isolatePhoneNumber('+62 811 3960 6800'),
  },
  general: {
    number: '6281139606800',
    humanReadable: isolatePhoneNumber('+62 811 3960 6800'),
  },
}

export function getIcsContactRegionForAirportCode(airportCode?: string): IcsContactRegion {
  return ICS_AIRPORT_CODES.find((airport) => airport.code === airportCode)?.contactRegion ?? 'general'
}

export const BALI_TRANSFER_SLUGS = [
  'bali-private-denpasar-airport-transfers',
  'bali-private-airport-transfers-for-accommodation-packages',
  'bali-denpasar-private-airport-transfers',
  'private-airport-transfers-for-bali-accommodation-packages',
  'bali-premium-private-denpasar-airport-transfers',
]

export function isBaliTransferSlug(slug?: string): boolean {
  return slug ? BALI_TRANSFER_SLUGS.includes(slug) : false
}

export function isBaliHotelToHotelTransfer(
  isBaliTransfer: boolean,
  placeId: string,
): boolean {
  return isBaliTransfer && placeId !== 'DPS'
}

export const ICS_AIRPORT_CODES: Array<{ code: string, contactRegion: IcsContactRegion }> = [
  // Bali, Indonesia
  { code: 'DPS', contactRegion: 'general' },

  // Singapore
  { code: 'SIN', contactRegion: 'general' },

  // Hong Kong
  { code: 'HKG', contactRegion: 'general' },

  // Malaysia
  { code: 'KUL', contactRegion: 'general' },
  { code: 'PEN', contactRegion: 'general' },
  { code: 'BKI', contactRegion: 'general' },
  { code: 'LGK', contactRegion: 'general' },
  { code: 'KCH', contactRegion: 'general' },
  { code: 'JHB', contactRegion: 'general' },
  { code: 'SZB', contactRegion: 'general' },
  { code: 'AOR', contactRegion: 'general' },
  { code: 'KBR', contactRegion: 'general' },
  { code: 'TGG', contactRegion: 'general' },
  { code: 'KUA', contactRegion: 'general' },
  { code: 'LBU', contactRegion: 'general' },
  { code: 'IPH', contactRegion: 'general' },

  // Thailand
  { code: 'BKK', contactRegion: 'thailand' },
  { code: 'DMK', contactRegion: 'thailand' },
  { code: 'CNX', contactRegion: 'thailand' },
  { code: 'HKT', contactRegion: 'thailand' },
  { code: 'HDY', contactRegion: 'thailand' },
  { code: 'KBV', contactRegion: 'thailand' },
  { code: 'USM', contactRegion: 'thailand' },
  { code: 'UTP', contactRegion: 'thailand' },
  { code: 'CEI', contactRegion: 'thailand' },
  { code: 'URT', contactRegion: 'thailand' },
  { code: 'UTH', contactRegion: 'thailand' },

  // Vietnam
  { code: 'HAN', contactRegion: 'vietnam' },
  { code: 'SGN', contactRegion: 'vietnam' },
  { code: 'DAD', contactRegion: 'vietnam' },
  { code: 'CXR', contactRegion: 'vietnam' },
  { code: 'HUI', contactRegion: 'vietnam' },
  { code: 'HPH', contactRegion: 'vietnam' },
  { code: 'VCA', contactRegion: 'vietnam' },
  { code: 'PQC', contactRegion: 'vietnam' },
  { code: 'VCL', contactRegion: 'vietnam' },
  { code: 'VDO', contactRegion: 'vietnam' },
]
